import React from 'react';
import PropTypes from 'prop-types';
import './ValidationSummary.css';

/**
 * ValidationSummary Component
 * Displays validation summary statistics as bar charts
 */
const ValidationSummary = ({ summary, validationResults }) => {
  if (!summary || !validationResults) {
    return null;
  }

  // Calculate Symphony API validation statistics
  const apiValidatedCount = validationResults.filter(
    r => r.matchStatus === 'api_validated'
  ).length;

  const apiFailedCount = validationResults.filter(
    r => r.matchStatus === 'api_validation_failed'
  ).length;

  const errorCount = validationResults.filter(
    r => r.matchStatus === 'error'
  ).length;

  // Calculate total and percentages for Symphony API validation
  const totalApiChecks = apiValidatedCount + apiFailedCount + errorCount;
  const apiValidatedPercentage = totalApiChecks > 0 ? (apiValidatedCount / totalApiChecks) * 100 : 0;
  const apiFailedPercentage = totalApiChecks > 0 ? (apiFailedCount / totalApiChecks) * 100 : 0;
  const errorPercentage = totalApiChecks > 0 ? (errorCount / totalApiChecks) * 100 : 0;

  // Calculate percentages for DynamoDB check
  const totalEmails = summary.totalEmails;
  const newEmailsPercentage = totalEmails > 0 ? (summary.newEmails / totalEmails) * 100 : 0;
  const existingEmailsPercentage = totalEmails > 0 ? (summary.existingEmails / totalEmails) * 100 : 0;

  // Extract failed emails with details
  const failedEmails = validationResults
    .filter(r => r.matchStatus === 'api_validation_failed' || r.matchStatus === 'error')
    .map(r => ({
      email: r.email,
      compareEmail: r.compareEmail,
      reason: r.matchStatus === 'api_validation_failed' ? 'Not found in Symphony' : 'Validation error',
      message: r.message,
      rowIndex: r.rowIndex
    }));

  // Extract emails that already exist in DB (from DynamoDB check)
  const existingEmailsList = validationResults
    .filter(r => r.hasDuplicates === true)
    .map(r => ({
      email: r.email,
      compareEmail: r.compareEmail,
      rowIndex: r.rowIndex,
      duplicateCheck: r.duplicateCheck
    }));

  return (
    <div className="validation-summary">
      <h3 className="summary-title">Validation Summary</h3>

      <div className="summary-grid">
        {/* Symphony API Validation Chart */}
        <div className="summary-section">
          <h4 className="section-title">Symphony API Validation</h4>
          <div className="chart-container">
            <div className="bar-chart-item">
              <div className="bar-label">
                <span className="label-text">✓ Validated</span>
                <span className="label-count">{apiValidatedCount}</span>
              </div>
              <div className="bar-wrapper">
                <div
                  className="bar bar-success"
                  style={{ width: `${apiValidatedPercentage}%` }}
                >
                  {apiValidatedPercentage > 15 && (
                    <span className="bar-percentage">{apiValidatedPercentage.toFixed(1)}%</span>
                  )}
                </div>
              </div>
            </div>

            <div className="bar-chart-item">
              <div className="bar-label">
                <span className="label-text">✗ Not Found</span>
                <span className="label-count">{apiFailedCount}</span>
              </div>
              <div className="bar-wrapper">
                <div
                  className="bar bar-error"
                  style={{ width: `${apiFailedPercentage}%` }}
                >
                  {apiFailedPercentage > 15 && (
                    <span className="bar-percentage">{apiFailedPercentage.toFixed(1)}%</span>
                  )}
                </div>
              </div>
            </div>

            {errorCount > 0 && (
              <div className="bar-chart-item">
                <div className="bar-label">
                  <span className="label-text">⚠ Errors</span>
                  <span className="label-count">{errorCount}</span>
                </div>
                <div className="bar-wrapper">
                  <div
                    className="bar bar-warning"
                    style={{ width: `${errorPercentage}%` }}
                  >
                    {errorPercentage > 15 && (
                      <span className="bar-percentage">{errorPercentage.toFixed(1)}%</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="chart-total">
            Total Checks: <strong>{totalApiChecks}</strong>
          </div>
        </div>

        {/* DynamoDB Duplicate Check Chart */}
        <div className="summary-section">
          <h4 className="section-title">Database Check</h4>
          <div className="chart-container">
            <div className="bar-chart-item">
              <div className="bar-label">
                <span className="label-text">New Emails</span>
                <span className="label-count">{summary.newEmails}</span>
              </div>
              <div className="bar-wrapper">
                <div
                  className="bar bar-success"
                  style={{ width: `${newEmailsPercentage}%` }}
                >
                  {newEmailsPercentage > 15 && (
                    <span className="bar-percentage">{newEmailsPercentage.toFixed(1)}%</span>
                  )}
                </div>
              </div>
            </div>

            <div className="bar-chart-item">
              <div className="bar-label">
                <span className="label-text">Already Exists</span>
                <span className="label-count">{summary.existingEmails}</span>
              </div>
              <div className="bar-wrapper">
                <div
                  className="bar bar-warning"
                  style={{ width: `${existingEmailsPercentage}%` }}
                >
                  {existingEmailsPercentage > 15 && (
                    <span className="bar-percentage">{existingEmailsPercentage.toFixed(1)}%</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="chart-total">
            Total Emails: <strong>{totalEmails}</strong>
          </div>
        </div>
      </div>

      {/* Failed Emails Section */}
      {failedEmails.length > 0 && (
        <div className="details-section">
          <h4 className="details-title">
            Failed Validation ({failedEmails.length})
          </h4>
          <div className="details-list">
            {failedEmails.map((item) => (
              <div key={`failed-${item.rowIndex}-${item.email}`} className="detail-item detail-failed">
                <div className="detail-header">
                  <span className="detail-icon">✗</span>
                  <span className="detail-email">{item.email}</span>
                  {item.compareEmail && (
                    <span className="detail-compare">→ {item.compareEmail}</span>
                  )}
                  <span className="detail-row">Row {item.rowIndex + 1}</span>
                </div>
                <div className="detail-reason">
                  {item.reason}
                  {item.message && <span className="detail-message">: {item.message}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Existing Emails Section */}
      {existingEmailsList.length > 0 && (
        <div className="details-section">
          <h4 className="details-title">
            Already in Database ({existingEmailsList.length})
          </h4>
          <div className="details-list">
            {existingEmailsList.map((item) => (
              <div key={`existing-${item.rowIndex}-${item.email}`} className="detail-item detail-existing">
                <div className="detail-header">
                  <span className="detail-icon">⚠</span>
                  <span className="detail-email">{item.email}</span>
                  {item.compareEmail && (
                    <span className="detail-compare">→ {item.compareEmail}</span>
                  )}
                  <span className="detail-row">Row {item.rowIndex + 1}</span>
                </div>
                <div className="detail-reason">
                  This email already exists in the database
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

ValidationSummary.propTypes = {
  summary: PropTypes.shape({
    totalEmails: PropTypes.number,
    newEmails: PropTypes.number,
    existingEmails: PropTypes.number,
    sameUserMatches: PropTypes.number,
    differentUserMatches: PropTypes.number
  }),
  validationResults: PropTypes.arrayOf(PropTypes.object)
};

export default ValidationSummary;
